import { useMemo, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { useEndorsePolicy, usePolicySchema } from '../api/policies';
import {
  coercePayload,
  fieldNameOfIssuePath,
  payloadFormOf,
  payloadSchemaOf,
  payloadValues,
} from '../model/payload-form';
import {
  editableRoles,
  hasPartnerRefusal,
  linksOf,
  partnerRefusalOf,
  seedPartnerValues,
  type PartnerFormValues,
} from '../model/partners';
import { isMember } from '../model/types';
import { ConflictNotice } from './ConflictNotice';
import { PartnerSection } from './PartnerSection';
import { PayloadFields } from './PayloadFields';

/**
 * Recording a change, effective from a date.
 *
 * An endorsement EXTENDS the timeline: it closes the current segment at `effectiveFrom` and opens
 * a new one at the next business version (decision S-8). It does not re-state anything — what was
 * true yesterday stays true — which is why this dialog asks for a future date and a payload, and
 * has no "reason" field. Correcting is the other operation, and it does.
 *
 * The form is built from the schema the policy's PIN resolved to, fetched from the service. That
 * is the whole reason the fields are not written out here: P-10442 is pinned to
 * `insurance_core.v1`, and a form built from the newest product schema would offer it a field its
 * own write path refuses.
 */
export function EndorseDialog({
  code,
  policy,
  open,
  onClose,
}: {
  readonly code: string;
  readonly policy: InstanceApi.Policy | undefined;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const schema = usePolicySchema(code);
  const endorse = useEndorsePolicy();

  const form = useMemo(() => payloadFormOf(schema.data?.schema), [schema.data?.schema]);
  const validator = useMemo(() => payloadSchemaOf(form), [form]);
  // The roles the PINNED tarif allows (SDD-011 P-8), minus the policyholder on a member (D7).
  const member = isMember(policy);
  const rules = useMemo(
    () => editableRoles(schema.data?.partnerRoles, member),
    [schema.data?.partnerRoles, member],
  );

  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [partners, setPartners] = useState<PartnerFormValues>({});
  const [submitted, setSubmitted] = useState(false);
  const [seeded, setSeeded] = useState(false);

  // Seeded from the CURRENT payload and partners, once the schema is in: an endorsement is an
  // edit of what is in force, not a blank form somebody has to retype.
  if (!seeded && schema.data !== undefined && policy?.data !== undefined) {
    setValues(payloadValues(policy.data));
    setPartners(
      seedPartnerValues(
        rules,
        (policy.partners ?? []).map((partner) => partner.link ?? {}),
      ),
    );
    setSeeded(true);
  }
  const refusal = partnerRefusalOf(endorse.error, rules, partners);

  const parsed = validator.safeParse(coercePayload(form, values));
  const fieldErrors: Record<string, string> = {};
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      // The path is `[group, field]` since C-26; the form keys its values the same way.
      const name = fieldNameOfIssuePath(issue.path);
      if (name !== '' && fieldErrors[name] === undefined) {
        fieldErrors[name] = t(`policies.endorse.errors.${issue.message}` as never, {
          defaultValue: t('policies.endorse.errors.invalid'),
        });
      }
    }
  }
  const dateMissing = effectiveFrom === '';

  const close = (): void => {
    setSubmitted(false);
    setSeeded(false);
    setEffectiveFrom('');
    endorse.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{t('policies.endorse.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('policies.endorse.description')}
          </Typography>

          <ConflictNotice conflict={endorse.conflict} />

          <TextField
            type="date"
            label={t('policies.endorse.effectiveFrom')}
            value={effectiveFrom}
            required
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(event) => {
              setEffectiveFrom(event.target.value);
            }}
            error={submitted && dateMissing}
            helperText={
              submitted && dateMissing
                ? t('policies.endorse.errors.required')
                : t('policies.endorse.effectiveFromHint')
            }
          />

          {schema.isPending ? (
            <Skeleton variant="rounded" height={200} />
          ) : schema.isError ? (
            <Alert severity="error">{t('policies.endorse.schemaError')}</Alert>
          ) : (
            <>
              <PayloadFields
                form={form}
                values={values}
                errors={submitted ? fieldErrors : {}}
                onChange={(name, value) => {
                  setValues((current) => ({ ...current, [name]: value }));
                }}
              />
              <PartnerSection
                rules={rules}
                values={partners}
                member={member}
                refusal={refusal}
                onChange={(roleErn, ids) => {
                  setPartners((current) => ({ ...current, [roleErn]: ids }));
                }}
              />
            </>
          )}

          {endorse.isSuccess && <Alert severity="success">{t('policies.endorse.recorded')}</Alert>}
          {endorse.isError && endorse.conflict === null && !hasPartnerRefusal(refusal) && (
            <Alert severity="error">{endorse.error.message}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{t('policies.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={endorse.isPending || schema.isPending}
          onClick={() => {
            setSubmitted(true);
            if (dateMissing || !parsed.success) return;
            // The COMPLETE partner list from the effective date on (P-7), always sent: a
            // partner change is an ordinary endorsement.
            endorse.mutate({
              code,
              effectiveFrom,
              data: parsed.data,
              partners: linksOf(rules, partners),
            });
          }}
        >
          {t('policies.endorse.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
